import { motion } from 'framer-motion';
import {
  SAM_AUDIENCE,
  SAM_TIER,
  SOM_AUDIENCE,
  SOM_TIER,
  TAM_GROUPS,
  TAM_TIER,
  formatARR,
  formatPounds,
  groupFirmTotal,
  groupTotal,
  tamTotal,
  type MarketAudience,
  type MarketGroup,
  type MarketTier,
} from '@/lib/roi/market-data';
import { CountUp } from './CountUp';

export function MarketSize({ embedded = false }: { embedded?: boolean } = {}) {
  return (
    <div className="flex flex-col gap-12 sm:gap-16">
      <TamSection embedded={embedded} />
      <FunnelSection
        tier={SAM_TIER}
        audience={SAM_AUDIENCE}
        embedded={embedded}
      />
      <FunnelSection
        tier={SOM_TIER}
        audience={SOM_AUDIENCE}
        embedded={embedded}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  TAM — approved UK I&R market table                                      */
/* ──────────────────────────────────────────────────────────────────────── */

/**
 * When `embedded` is true, the section drops its own max-w-6xl and px-8
 * wrapper so it can sit naturally inside a parent that already provides
 * a content column (e.g. the /pitch disclosure panels).
 */
const standaloneSectionClass = 'mx-auto w-full max-w-6xl px-8';

function TamSection({ embedded }: { embedded?: boolean }) {
  return (
    <section className={embedded ? undefined : standaloneSectionClass}>
      <TierHeader tier={TAM_TIER} totalCheck={formatARR(tamTotal())} />
      <div className="mt-10">
        {TAM_GROUPS.map((group, index) => (
          <MarketGroupCard key={group.label} group={group} index={index} />
        ))}
      </div>
    </section>
  );
}

function MarketGroupCard({
  group,
  index,
}: {
  group: MarketGroup;
  index: number;
}) {
  const subtotal = groupTotal(group);
  const firmTotal = groupFirmTotal(group);

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      whileHover={{ y: -3 }}
      transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
        delay: index * 0.08,
      }}
      className="pitch-card pitch-card-interactive flex min-w-0 flex-col gap-5 rounded-xl p-5 sm:p-6"
    >
      <header className="flex flex-col gap-2">
        <span className="text-xs font-medium uppercase tracking-[0.12em] text-ink-dim">
          {group.label}
        </span>
        <div className="flex items-baseline gap-2">
          <span className="font-semibold tabular-nums text-ink-strong [font-size:32px] [line-height:1]">
            {formatARR(subtotal)}
          </span>
          <span className="text-xs text-ink-dim">subtotal</span>
        </div>
      </header>

      <div className="min-w-0 overflow-x-auto border-t border-border-subtle pt-4">
        <p className="mb-3 text-[11px] uppercase tracking-[0.12em] text-ink-dim sm:hidden">
          Swipe to see licenses, ACV and TAM
        </p>
        <table className="w-full min-w-[700px] border-collapse text-left text-sm">
          <caption className="sr-only">{group.label} breakdown</caption>
          <thead>
            <tr className="border-b border-border-divider text-[11px] font-medium uppercase tracking-[0.12em] text-ink-dim">
              <th scope="col" className="pb-3 pr-5">
                Tier
              </th>
              <th scope="col" className="px-5 pb-3 text-right">
                Firms
              </th>
              <th scope="col" className="px-5 pb-3 text-right">
                Licenses
              </th>
              <th scope="col" className="px-5 pb-3 text-right">
                ACV
              </th>
              <th scope="col" className="pb-3 pl-5 text-right">
                UK TAM
              </th>
            </tr>
          </thead>
          <tbody>
            {group.segments.map((segment) => (
              <tr
                key={segment.label}
                className="border-b border-border-divider text-ink-strong"
              >
                <th
                  scope="row"
                  className="whitespace-nowrap py-3.5 pr-5 font-medium"
                >
                  {segment.label}
                </th>
                <td className="px-5 py-3.5 text-right tabular-nums">
                  {segment.firms?.toLocaleString('en-GB')}
                </td>
                <td className="px-5 py-3.5 text-right tabular-nums">
                  {segment.licensesPerFirm?.toLocaleString('en-GB')}
                </td>
                <td className="px-5 py-3.5 text-right tabular-nums text-ink-muted">
                  {segment.averageAcv !== undefined
                    ? formatPounds(segment.averageAcv)
                    : null}
                </td>
                <td className="py-3.5 pl-5 text-right font-medium tabular-nums text-brand-500">
                  {formatARR(segment.arr, segment.displayDecimals)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="text-ink-strong">
              <th scope="row" className="pr-5 pt-4 font-semibold">
                Total
              </th>
              <td className="px-5 pt-4 text-right font-semibold tabular-nums">
                {firmTotal.toLocaleString('en-GB')}
              </td>
              <td className="px-5 pt-4 text-right">
                <span className="sr-only">Not applicable</span>
              </td>
              <td className="px-5 pt-4 text-right">
                <span className="sr-only">Not applicable</span>
              </td>
              <td className="pl-5 pt-4 text-right font-semibold tabular-nums text-brand-500">
                {formatARR(subtotal, 2)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </motion.article>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  SAM / SOM — single card with target customers + reach                    */
/* ──────────────────────────────────────────────────────────────────────── */

function FunnelSection({
  tier,
  audience,
  embedded,
}: {
  tier: MarketTier;
  audience: MarketAudience;
  embedded?: boolean;
}) {
  // 4 items → 4-col strip on sm+ (2×2 on mobile); 3 items → 3-col strip.
  // Tailwind needs class strings statically.
  const stripGridClass =
    audience.items.length === 4
      ? 'grid-cols-2 sm:grid-cols-4'
      : 'grid-cols-1 sm:grid-cols-3';

  return (
    <section className={embedded ? undefined : standaloneSectionClass}>
      <TierHeader tier={tier} />
      <div className="mt-10 flex flex-col gap-4">
        <span className="text-xs font-medium uppercase tracking-[0.14em] text-ink-dim">
          {audience.heading}
        </span>
        {/*
          Column strip: a single bordered panel with internal hairline
          dividers. We use `gap-px` + container `bg-border-subtle` so that
          a 1px container background bleeds through between cells in both
          axes — gives clean dividers without per-item border logic.
        */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className={`pitch-strip grid gap-px overflow-hidden rounded-2xl border border-border bg-border-subtle ${stripGridClass}`}
        >
          {audience.items.map((item, i) => (
            <FocusColumn
              key={item}
              index={i}
              total={audience.items.length}
              text={item}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function FocusColumn({
  index,
  total,
  text,
}: {
  index: number;
  total: number;
  text: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.15 + index * 0.06,
      }}
      className="pitch-cell pitch-cell-interactive flex flex-col gap-3 p-6 sm:p-7"
    >
      <span className="text-[11px] font-medium uppercase tabular-nums tracking-[0.22em] text-brand-500">
        {String(index + 1).padStart(2, '0')}{' '}
        <span className="text-ink-dim">/ {String(total).padStart(2, '0')}</span>
      </span>
      <span className="text-[15px] font-medium leading-snug text-ink-strong sm:text-base">
        {text}
      </span>
    </motion.div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  Shared tier heading: acronym pill + headline ARR + scope                 */
/* ──────────────────────────────────────────────────────────────────────── */

function TierHeader({
  tier,
  totalCheck,
}: {
  tier: MarketTier;
  /** Optional second figure shown when the headline is rounded — confirms the
      sum of the segments below matches what's claimed at the top. */
  totalCheck?: string;
}) {
  return (
    <motion.header
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="flex flex-col gap-3"
    >
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
        <span className="inline-flex items-center rounded-md border border-accent-blue-border bg-accent-blue-bg px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-brand-bright">
          {tier.acronym}
        </span>
        <h2 className="text-2xl font-semibold leading-tight text-ink-strong sm:text-3xl">
          {tier.title}
        </h2>
        <span className="font-semibold tabular-nums text-brand-500 [font-size:28px] [line-height:1] sm:[font-size:32px]">
          {tier.headlineRange ? (
            <>
              <CountUp
                target={tier.headlineRange[0]}
                duration={1.4}
                delay={0.3}
                formatter={formatARR}
              />
              –
              <CountUp
                target={tier.headlineRange[1]}
                duration={1.4}
                delay={0.45}
                formatter={formatARR}
              />
              {' ARR'}
            </>
          ) : tier.headlineValue !== undefined ? (
            <CountUp
              target={tier.headlineValue}
              duration={1.6}
              delay={0.3}
              formatter={(n) => `${formatARR(n)} ARR`}
            />
          ) : (
            tier.headline
          )}
        </span>
        {totalCheck ? (
          <span className="text-xs text-ink-dim">
            ({totalCheck} from segments)
          </span>
        ) : null}
      </div>
      <p className="max-w-2xl text-sm leading-relaxed text-ink-muted">
        {tier.scope}
      </p>
    </motion.header>
  );
}
